/**
 * Kern der Scroll-Engine: verwaltet einen Fullpage-Wrapper mit seinen
 * Sektionen in zwei Betriebsmodi.
 *
 * - native:    CSS Scroll Snap; „scroll" = frei + Einrasten,
 *              „smooth" = diskretes, JS-animiertes Scrollen.
 * - transform: gestapelte Sektionen, GPU-Übergänge
 *              (fade/slide/zoom/parallax/cover/reveal).
 */

import { Navigation } from './Navigation.js';
import { HashRouter } from './HashRouter.js';
import { WheelHandler } from './input/Wheel.js';
import { TouchHandler } from './input/Touch.js';
import { KeyboardHandler } from './input/Keyboard.js';
import {
	animateValue,
	clamp,
	createLogger,
	debounce,
	dispatch,
	formatLabel,
	normalizeWheelDelta,
	prefersReducedMotion,
} from './utils.js';

const TRANSITION_ANIMATIONS = [ 'fade', 'slide', 'zoom', 'parallax', 'cover', 'reveal' ];

const DEFAULTS = {
	direction: 'vertical',
	animation: 'scroll',
	speed: 700,
	snap: 'mandatory',
	loop: false,
	hash: true,
	dots: true,
	arrows: true,
	wheel: true,
	touch: true,
	keyboard: true,
	tabletMode: 'fullpage',
	mobileMode: 'normal',
	breakpointTablet: 1024,
	breakpointMobile: 767,
	debug: false,
};

export class FullpageEngine {
	constructor( wrapper, globalConfig ) {
		this.wrapper = wrapper;
		this.i18n = globalConfig.i18n || {};

		let overrides = {};

		try {
			overrides = JSON.parse( wrapper.dataset.sfps || '{}' );
		} catch ( e ) {
			overrides = {};
		}

		this.config = { ...DEFAULTS, ...( globalConfig.defaults || {} ), ...overrides };
		this.log = createLogger( this.config.debug );

		this.axis = this.config.direction === 'horizontal' ? 'h' : 'v';

		const inner = wrapper.querySelector( ':scope > .e-con-inner' );
		this.root = inner || wrapper;

		// Vertikal scrollt der Wrapper selbst; horizontal scrollt der
		// Container, der die Sektionen direkt enthält (Track).
		this.scroller = this.axis === 'h' ? this.root : wrapper;
		this.sections = Array.from( this.root.children ).filter( ( el ) =>
			el.matches( '.e-con, .elementor-element' )
		);

		this.index = 0;
		this.active = false;
		this.locked = false;
		this.baseAnimation = 'scroll';
		this.currentAnimation = 'scroll';
		this.mode = 'native';
		this.delayTimer = 0;
		this.settleTimer = 0;
		this.transitionTimer = 0;
		this.cancelScroll = null;
		this.scrollRaf = 0;
		this.nav = null;
		this.hash = null;
		this.wheel = null;
		this.touch = null;
		this.keyboard = null;

		if ( this.sections.length < 2 ) {
			this.log( 'Wrapper übersprungen: weniger als zwei Sektionen.', wrapper );
			return;
		}

		this.prepareSections();
		this.evaluateMode();

		this.onResize = debounce( () => this.evaluateResponsive(), 150 );
		window.addEventListener( 'resize', this.onResize );

		this.evaluateResponsive();
		this.log( 'Engine initialisiert:', { mode: this.mode, config: this.config } );
	}

	/* ------------------------------------------------------------------ *
	 * Setup
	 * ------------------------------------------------------------------ */

	prepareSections() {
		this.anchors = [];
		this.hasLockedSections = false;

		this.sections.forEach( ( section, i ) => {
			section.classList.add( 'sfps-section' );
			section.setAttribute( 'tabindex', '-1' );
			section.setAttribute( 'role', 'region' );

			const anchor = section.dataset.sfpsAnchor || '';
			this.anchors.push( anchor || null );

			if ( anchor && ! section.id ) {
				section.id = anchor;
			}

			if ( ! section.hasAttribute( 'aria-label' ) ) {
				section.setAttribute(
					'aria-label',
					anchor ? anchor.replace( /[-_]+/g, ' ' ) : formatLabel( this.i18n.section, i + 1 )
				);
			}

			if ( section.dataset.sfpsLock ) {
				this.hasLockedSections = true;
			}
		} );

		this.parallaxSections = this.sections.filter( ( s ) => s.dataset.sfpsParallax );
	}

	evaluateMode() {
		const animation = prefersReducedMotion() ? 'scroll' : this.config.animation;

		this.baseAnimation = animation;
		this.currentAnimation = animation;
		this.mode = TRANSITION_ANIMATIONS.includes( animation ) ? 'transform' : 'native';
	}

	/**
	 * Prüft Breakpoints und (de)aktiviert die Engine entsprechend.
	 */
	evaluateResponsive() {
		const width = window.innerWidth;
		let mode = 'fullpage';

		if ( width <= this.config.breakpointMobile ) {
			mode = this.config.mobileMode;
		} else if ( width <= this.config.breakpointTablet ) {
			mode = this.config.tabletMode;
		}

		if ( mode === 'auto' ) {
			mode = window.matchMedia( '(pointer: coarse)' ).matches ? 'normal' : 'fullpage';
		}

		if ( mode === 'fullpage' ) {
			this.activate();
		} else {
			this.deactivate();
		}
	}

	activate() {
		if ( this.active ) {
			return;
		}

		this.active = true;
		this.wrapper.classList.remove( 'sfps-normal' );
		this.wrapper.classList.add( 'sfps-ready', `sfps-mode-${ this.mode }` );
		this.wrapper.style.setProperty( '--sfps-speed', `${ this.config.speed }ms` );

		if ( this.axis === 'h' ) {
			this.wrapper.classList.add( 'sfps-axis-h' );
			this.scroller.classList.add( 'sfps-h-track' );
		}

		if ( this.mode === 'native' ) {
			this.wrapper.classList.add(
				this.config.snap === 'proximity' ? 'sfps-snap-proximity' : 'sfps-snap-mandatory'
			);
			this.bindNativeScroll();
		} else {
			this.wrapper.classList.add( `sfps-anim-${ this.currentAnimation }` );
			this.applyTransformState();

			// Übergänge erst nach dem ersten Paint aktivieren,
			// damit der Startzustand nicht animiert.
			requestAnimationFrame( () =>
				requestAnimationFrame( () => this.wrapper.classList.add( 'sfps-animate' ) )
			);
		}

		this.buildNavigation();
		this.bindInputs();

		if ( this.config.hash ) {
			this.hash = new HashRouter( {
				anchors: this.anchors,
				onNavigate: ( index ) => this.goTo( index, { source: 'hash' } ),
			} );

			const initial = this.hash.initialIndex();

			if ( initial !== null && initial !== this.index ) {
				this.goTo( initial, { instant: true, source: 'init' } );
			}
		}

		// Nach einem Responsive-Wechsel zur gemerkten Sektion zurückkehren.
		if ( this.index > 0 ) {
			this.goTo( this.index, { instant: true, source: 'init', force: true } );
		}

		this.updateNav();
	}

	deactivate() {
		const wasActive = this.active;
		this.active = false;

		this.wrapper.classList.add( 'sfps-normal' );
		this.wrapper.classList.remove(
			'sfps-ready',
			'sfps-mode-native',
			'sfps-mode-transform',
			'sfps-axis-h',
			'sfps-snap-mandatory',
			'sfps-snap-proximity',
			'sfps-animate',
			`sfps-anim-${ this.currentAnimation }`
		);
		this.scroller.classList.remove( 'sfps-h-track' );

		if ( ! wasActive ) {
			return;
		}

		this.sections.forEach( ( section ) => {
			section.classList.remove( 'sfps-active', 'sfps-above', 'sfps-below' );
			section.style.backgroundPositionY = '';
			section.style.backgroundPositionX = '';
		} );

		clearTimeout( this.delayTimer );
		clearTimeout( this.settleTimer );
		clearTimeout( this.transitionTimer );
		cancelAnimationFrame( this.scrollRaf );

		if ( this.cancelScroll ) {
			this.cancelScroll();
			this.cancelScroll = null;
		}

		this.locked = false;

		if ( this.nav ) {
			this.nav.destroy();
			this.nav = null;
		}

		if ( this.hash ) {
			this.hash.destroy();
			this.hash = null;
		}

		if ( this.wheel ) {
			this.wheel.destroy();
			this.wheel = null;
		}

		if ( this.touch ) {
			this.touch.destroy();
			this.touch = null;
		}

		if ( this.keyboard ) {
			this.keyboard.destroy();
			this.keyboard = null;
		}

		this.unbindNativeScroll();
		this.log( 'Engine deaktiviert (Responsive-Modus „normal").' );
	}

	destroy() {
		this.deactivate();
		window.removeEventListener( 'resize', this.onResize );
		this.wrapper.classList.remove( 'sfps-normal' );

		this.sections.forEach( ( section ) => {
			section.classList.remove( 'sfps-section' );
			section.removeAttribute( 'tabindex' );
		} );
	}

	/* ------------------------------------------------------------------ *
	 * Navigation & Eingaben
	 * ------------------------------------------------------------------ */

	buildNavigation() {
		if ( ! this.config.dots && ! this.config.arrows ) {
			return;
		}

		this.nav = new Navigation( this.wrapper, {
			axis: this.axis,
			count: this.sections.length,
			dots: this.config.dots,
			arrows: this.config.arrows,
			loop: this.config.loop,
			navLabel: this.i18n.nav || 'Sections',
			itemLabel: this.i18n.section,
			prevLabel: this.i18n.prevSection || 'Previous section',
			nextLabel: this.i18n.nextSection || 'Next section',
			labels: this.anchors.map( ( a ) => ( a ? a.replace( /[-_]+/g, ' ' ) : null ) ),
			onSelect: ( index ) => this.goTo( index, { source: 'nav', focus: true } ),
			onStep: ( direction ) => this.step( direction, 'nav' ),
		} );
	}

	bindInputs() {
		this.wheel = new WheelHandler( this.wrapper, {
			verdict: ( event ) => this.wheelVerdict( event ),
			isLocked: () => this.locked,
			onNavigate: ( direction ) => this.step( direction, 'wheel' ),
		} );

		this.touch = new TouchHandler( this.wrapper, {
			axis: this.axis,
			verdict: () => this.touchVerdict(),
			isLocked: () => this.locked,
			onNavigate: ( direction ) => this.step( direction, 'touch' ),
		} );

		this.keyboard = new KeyboardHandler( {
			isApplicable: () => this.active && this.config.keyboard && this.isInViewport(),
			onNavigate: ( direction ) => this.step( direction, 'keyboard' ) || this.locked,
			onEdge: ( pos ) =>
				this.goTo( pos === 'home' ? 0 : this.sections.length - 1, {
					source: 'keyboard',
					focus: true,
				} ),
			// Bei horizontaler Richtung steuern Links/Rechts die Engine selbst,
			// sonst einen verschachtelten horizontalen Slider in der Sektion.
			onHorizontal: ( direction ) =>
				this.axis === 'h'
					? this.step( direction, 'keyboard' ) || this.locked
					: this.stepHorizontal( direction ),
		} );
	}

	wheelVerdict( event ) {
		if ( ! this.active ) {
			return 'native';
		}

		if ( ! this.config.wheel ) {
			return 'block';
		}

		if ( this.isSectionLocked() ) {
			return 'block';
		}

		if ( this.mode === 'transform' ) {
			// Überlange Sektionen dürfen erst intern zu Ende scrollen.
			if ( this.sectionCanScroll( normalizeWheelDelta( event ) ) ) {
				return 'native';
			}

			return 'navigate';
		}

		if ( this.axis === 'h' ) {
			// Echte horizontale Trackpad-Gesten scrollt der Browser
			// nativ über den Track (CSS Snap X).
			if ( Math.abs( event.deltaX ) > Math.abs( event.deltaY ) ) {
				return 'native';
			}

			// Überlange Sektionen dürfen intern vertikal zu Ende scrollen.
			if ( this.sectionCanScroll( normalizeWheelDelta( event ) ) ) {
				return 'native';
			}

			// Vertikales Mausrad in horizontale Navigation übersetzen.
			return 'navigate';
		}

		// Nativer Modus: „smooth" steuert das Mausrad selbst,
		// „scroll" überlässt es dem Browser (CSS Snap).
		if ( this.baseAnimation === 'smooth' ) {
			return 'navigate';
		}

		// Boundary-Handoff: Events, die ein horizontaler Slider am Anfang/
		// Ende durchreicht, diskret navigieren — natives Scroll-Chaining
		// aus einem Scroll-Container heraus ist browserseitig unzuverlässig.
		if ( event.target instanceof Element && event.target.closest( '.sfps-horizontal' ) ) {
			return 'navigate';
		}

		return 'native';
	}

	touchVerdict() {
		if ( ! this.active ) {
			return 'native';
		}

		if ( ! this.config.touch || this.isSectionLocked() ) {
			return 'block';
		}

		// Im nativen Modus übernimmt CSS Scroll Snap die Touch-Gesten
		// (vertikal am Wrapper, horizontal am Track).
		return this.mode === 'transform' ? 'navigate' : 'native';
	}

	isSectionLocked() {
		return Boolean( this.sections[ this.index ].dataset.sfpsLock );
	}

	sectionCanScroll( delta ) {
		const section = this.sections[ this.index ];
		const maxScroll = section.scrollHeight - section.clientHeight;

		if ( maxScroll < 2 ) {
			return false;
		}

		return delta > 0 ? section.scrollTop < maxScroll - 1 : section.scrollTop > 1;
	}

	isInViewport() {
		const rect = this.wrapper.getBoundingClientRect();
		return rect.top < window.innerHeight && rect.bottom > 0;
	}

	stepHorizontal( direction ) {
		const slider = this.sections[ this.index ].querySelector( '.sfps-horizontal' );

		if ( slider && slider.__sfpsSlider ) {
			return slider.__sfpsSlider.step( direction, 'keyboard' );
		}

		return false;
	}

	/* ------------------------------------------------------------------ *
	 * Navigation zwischen Sektionen
	 * ------------------------------------------------------------------ */

	step( direction, source ) {
		if ( this.locked ) {
			return true;
		}

		if (
			this.isSectionLocked() &&
			( source === 'wheel' || source === 'touch' || source === 'keyboard' )
		) {
			return true;
		}

		let target = this.index + direction;

		if ( this.config.loop ) {
			target = ( target + this.sections.length ) % this.sections.length;
		} else if ( target < 0 || target >= this.sections.length ) {
			return false;
		}

		return this.goTo( target, { source } );
	}

	/**
	 * Navigiert zu einer Sektion (Index oder Anchor-String).
	 *
	 * @return {boolean} Ob die Navigation gestartet wurde.
	 */
	goTo( target, options = {} ) {
		const opts = { source: 'api', instant: false, focus: false, force: false, ...options };

		let index = target;

		if ( typeof target === 'string' ) {
			index = this.anchors.indexOf( target );

			if ( index < 0 ) {
				this.log( 'Unbekannter Anchor:', target );
				return false;
			}
		}

		index = clamp( index, 0, this.sections.length - 1 );

		if ( ! this.active || ( this.locked && ! opts.instant ) ) {
			return false;
		}

		if ( index === this.index && ! opts.force ) {
			return false;
		}

		const from = this.index;
		const direction = index > from ? 'down' : 'up';

		if (
			! dispatch(
				this.wrapper,
				'sfps:beforeSection',
				{ from, to: index, direction, wrapper: this.wrapper, source: opts.source },
				true
			)
		) {
			return false;
		}

		const delay = opts.instant
			? 0
			: parseInt( this.sections[ index ].dataset.sfpsDelay || '0', 10 );

		this.locked = true;
		clearTimeout( this.delayTimer );

		if ( delay > 0 ) {
			this.delayTimer = setTimeout(
				() => this.performTransition( from, index, opts ),
				delay
			);
		} else {
			this.performTransition( from, index, opts );
		}

		return true;
	}

	performTransition( from, to, opts ) {
		this.index = to;
		this.updateNav();

		if ( this.mode === 'native' ) {
			this.performScroll( from, to, opts );
		} else {
			this.performTransform( from, to, opts );
		}
	}

	performScroll( from, to, opts ) {
		const horizontal = this.axis === 'h';
		const sectionRect = this.sections[ to ].getBoundingClientRect();
		const scrollerRect = this.scroller.getBoundingClientRect();
		const target = horizontal
			? sectionRect.left - scrollerRect.left + this.scroller.scrollLeft
			: sectionRect.top - scrollerRect.top + this.scroller.scrollTop;

		const setScroll = ( value ) => {
			if ( horizontal ) {
				this.scroller.scrollLeft = value;
			} else {
				this.scroller.scrollTop = value;
			}
		};

		if ( this.cancelScroll ) {
			this.cancelScroll();
			this.cancelScroll = null;
		}

		if ( opts.instant || prefersReducedMotion() ) {
			setScroll( target );
			this.finishTransition( from, to, opts );
			return;
		}

		if ( this.baseAnimation === 'smooth' ) {
			// Eigene rAF-Animation für eine konsistente Geschwindigkeit.
			this.cancelScroll = animateValue( {
				from: horizontal ? this.scroller.scrollLeft : this.scroller.scrollTop,
				to: target,
				duration: this.config.speed,
				onUpdate: setScroll,
				onDone: () => {
					this.cancelScroll = null;
					this.finishTransition( from, to, opts );
				},
			} );
			return;
		}

		// Standard Scroll: natives smooth scrolling.
		this.scroller.scrollTo(
			horizontal
				? { left: target, behavior: 'smooth' }
				: { top: target, behavior: 'smooth' }
		);
		this.awaitScrollEnd( () => this.finishTransition( from, to, opts ) );
	}

	awaitScrollEnd( done ) {
		let settled = false;
		let fallback = 0;

		const finish = () => {
			if ( settled ) {
				return;
			}

			settled = true;
			clearTimeout( fallback );
			this.scroller.removeEventListener( 'scrollend', finish );
			done();
		};

		if ( 'onscrollend' in window ) {
			this.scroller.addEventListener( 'scrollend', finish, { once: true } );
			fallback = setTimeout( finish, 1200 );
		} else {
			fallback = setTimeout( finish, 700 );
		}
	}

	performTransform( from, to, opts ) {
		const sectionAnimation = this.sections[ to ].dataset.sfpsAnimation;
		const nextAnimation =
			sectionAnimation && TRANSITION_ANIMATIONS.includes( sectionAnimation )
				? sectionAnimation
				: this.baseAnimation;

		this.setTransformAnimation( nextAnimation );
		this.applyTransformState();

		const duration = opts.instant ? 0 : this.config.speed;

		clearTimeout( this.transitionTimer );

		if ( duration === 0 ) {
			this.wrapper.classList.remove( 'sfps-animate' );
			requestAnimationFrame( () =>
				requestAnimationFrame( () => this.wrapper.classList.add( 'sfps-animate' ) )
			);
			this.finishTransition( from, to, opts );
			return;
		}

		this.transitionTimer = setTimeout(
			() => this.finishTransition( from, to, opts ),
			duration + 50
		);
	}

	/**
	 * Wechselt die Animationsklasse ohne sichtbare Zwischenanimation
	 * (für individuelle Animationen einzelner Sektionen).
	 */
	setTransformAnimation( animation ) {
		if ( animation === this.currentAnimation ) {
			return;
		}

		this.wrapper.classList.remove( 'sfps-animate', `sfps-anim-${ this.currentAnimation }` );
		this.wrapper.classList.add( `sfps-anim-${ animation }` );
		this.currentAnimation = animation;

		// Reflow erzwingen, damit die neue Basis ohne Übergang gilt.
		void this.wrapper.offsetWidth;
		this.wrapper.classList.add( 'sfps-animate' );
	}

	applyTransformState() {
		this.sections.forEach( ( section, i ) => {
			section.classList.toggle( 'sfps-active', i === this.index );
			section.classList.toggle( 'sfps-above', i < this.index );
			section.classList.toggle( 'sfps-below', i > this.index );
		} );
	}

	finishTransition( from, to, opts ) {
		this.locked = false;

		if ( this.hash && opts.source !== 'hash' && opts.source !== 'init' ) {
			this.hash.update( to );
		}

		if ( opts.focus || opts.source === 'keyboard' ) {
			this.sections[ to ].focus( { preventScroll: true } );
		}

		dispatch( this.wrapper, 'sfps:afterSection', {
			from,
			to,
			direction: to > from ? 'down' : 'up',
			wrapper: this.wrapper,
			source: opts.source,
		} );

		this.log( 'Sektion aktiv:', to );
	}

	updateNav() {
		if ( this.nav ) {
			this.nav.setActive( this.index );
		}
	}

	/* ------------------------------------------------------------------ *
	 * Nativer Modus: Scroll-Beobachtung + Parallax
	 * ------------------------------------------------------------------ */

	bindNativeScroll() {
		this.onScroll = () => {
			if ( this.scrollRaf ) {
				return;
			}

			this.scrollRaf = requestAnimationFrame( () => {
				this.scrollRaf = 0;
				this.handleNativeScroll();
			} );
		};

		this.scroller.addEventListener( 'scroll', this.onScroll, { passive: true } );
		this.handleNativeScroll();
	}

	unbindNativeScroll() {
		if ( this.onScroll ) {
			this.scroller.removeEventListener( 'scroll', this.onScroll );
			this.onScroll = null;
		}
	}

	handleNativeScroll() {
		this.updateParallax();

		if ( this.locked ) {
			return;
		}

		// Aktive Sektion = die, die die Mitte des Scroll-Containers enthält.
		const horizontal = this.axis === 'h';
		const center = horizontal
			? this.scroller.scrollLeft + this.scroller.clientWidth / 2
			: this.scroller.scrollTop + this.scroller.clientHeight / 2;
		const scrollerRect = this.scroller.getBoundingClientRect();
		let current = this.index;

		for ( let i = 0; i < this.sections.length; i++ ) {
			const rect = this.sections[ i ].getBoundingClientRect();
			const start = horizontal
				? rect.left - scrollerRect.left + this.scroller.scrollLeft
				: rect.top - scrollerRect.top + this.scroller.scrollTop;
			const size = horizontal ? rect.width : rect.height;

			if ( center >= start && center < start + size ) {
				current = i;
				break;
			}
		}

		if ( current === this.index ) {
			return;
		}

		const from = this.index;
		this.index = current;
		this.updateNav();

		dispatch( this.wrapper, 'sfps:beforeSection', {
			from,
			to: current,
			direction: current > from ? 'down' : 'up',
			wrapper: this.wrapper,
			source: 'scroll',
		} );

		// Hash und afterSection erst, wenn das Scrollen zur Ruhe kommt.
		clearTimeout( this.settleTimer );
		this.settleTimer = setTimeout( () => {
			if ( this.hash ) {
				this.hash.update( this.index );
			}

			dispatch( this.wrapper, 'sfps:afterSection', {
				from,
				to: this.index,
				direction: this.index > from ? 'down' : 'up',
				wrapper: this.wrapper,
				source: 'scroll',
			} );
		}, 150 );
	}

	updateParallax() {
		if ( ! this.parallaxSections.length ) {
			return;
		}

		const scrollerRect = this.scroller.getBoundingClientRect();

		this.parallaxSections.forEach( ( section ) => {
			const rect = section.getBoundingClientRect();

			if ( this.axis === 'h' ) {
				const offset = ( rect.left - scrollerRect.left ) * -0.4;
				section.style.backgroundPositionX = `calc(50% + ${ Math.round( offset ) }px)`;
			} else {
				const offset = ( rect.top - scrollerRect.top ) * -0.4;
				section.style.backgroundPositionY = `calc(50% + ${ Math.round( offset ) }px)`;
			}
		} );
	}
}
